<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3 pb-4">
      <v-breadcrumbs :items="breadcrumbs" class="px-4 pr-5 mb-3 pt-4 pb-2">
        <template v-slot:divider>
          <v-icon>chevron_right</v-icon>
        </template>
        <template v-slot:item="{ item }">
          <router-link
            v-if="item.to"
            :to="item.to"
            class="text-decoration-none primary--text body-1"
          >{{ item.text }}</router-link>
          <span v-else class="title-style">{{ item.text }}</span>
        </template>
      </v-breadcrumbs>
      <template v-if="run">
        <v-row class="px-4">
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Client</div>
            <div class="body-2">{{ run.clientName || '—' }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Source</div>
            <div class="body-2">{{ run.vmsIntegrationName || 'Upload' }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Trigger</div>
            <div class="body-2">{{ run.triggerName || '—' }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Status</div>
            <v-chip
              small
              label
              :color="statusAttrs(run.statusId).color"
              :dark="statusAttrs(run.statusId).dark"
            >{{ run.statusName || '—' }}</v-chip>
          </v-col>
        </v-row>
        <v-row class="px-4 mt-0">
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Imported / Failed / Skipped / Total</div>
            <div class="body-2">{{ run.importedRows || 0 }} / {{ run.failedRows || 0 }} / {{ runSkippedRows }} / {{ run.totalRows || 0 }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Started</div>
            <div class="body-2">{{ run.startedAt | formatDate }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text">Finished</div>
            <div class="body-2">{{ run.finishedAt | formatDate }}</div>
          </v-col>
        </v-row>
        <v-row v-if="run.error" class="px-4 mt-0">
          <v-col cols="12">
            <div class="caption grey--text">Error</div>
            <div class="body-2 red--text">{{ run.error }}</div>
          </v-col>
        </v-row>
      </template>
    </div>

    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="batchHeaders"
          :items="batches"
          item-key="id"
          show-expand
          :expanded.sync="expanded"
          hide-default-footer
          :items-per-page="-1"
          class="elevation-2"
          @item-expanded="onBatchExpanded"
        >
          <template v-slot:item.fileName="{ item }">
            {{ item.uploadedFileName || item.sourceFileName || '—' }}
          </template>
          <template v-slot:item.statusName="{ item }">
            <v-chip
              x-small
              label
              :color="statusAttrs(item.statusId).color"
              :dark="statusAttrs(item.statusId).dark"
            >{{ item.statusName }}</v-chip>
          </template>
          <template v-slot:item.rows="{ item }">
            {{ item.importedRows || 0 }} / {{ item.failedRows || 0 }} / {{ item.skippedRows || 0 }} / {{ item.totalRows || 0 }}
          </template>
          <template v-slot:item.createdAt="{ item }">
            {{ item.createdAt | formatDate }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn
              v-if="isValidated(item)"
              small
              color="primary"
              class="ma-1"
              @click.stop="importBatchAndRefresh(item.id)"
            >Import</v-btn>
            <v-btn
              v-if="isImported(item)"
              small
              outlined
              color="primary"
              class="ma-1"
              @click.stop="recheckAssignmentsAndRefresh(item.id)"
            >Re-check assignments</v-btn>
            <v-btn
              v-if="canReRead(item)"
              small
              outlined
              color="primary"
              class="ma-1"
              @click.stop="rereadAndRefresh(item.id)"
            >Re-read file</v-btn>
          </template>
          <template v-slot:item.downloads="{ item }">
            <v-tooltip bottom v-if="hasOriginal(item)">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click.stop="openDownload(item.id, 'original')">
                  <v-icon small>cloud_download</v-icon>
                </v-btn>
              </template>
              <span>Download original file</span>
            </v-tooltip>
            <v-tooltip bottom v-if="hasPayload(item)">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click.stop="openDownload(item.id, 'payload')">
                  <v-icon small>description</v-icon>
                </v-btn>
              </template>
              <span>Download normalized payload</span>
            </v-tooltip>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td class="py-4" :colspan="headers.length">
              <v-row class="px-4 mb-1" align="center">
                <v-col cols="12" sm="4">
                  <v-select
                    :value="batchStateFor(item.id).outcomeId"
                    :items="outcomeOptions"
                    item-text="name"
                    item-value="id"
                    label="All Outcomes"
                    clearable
                    outlined
                    dense
                    hide-details
                    @change="changeOutcome(item.id, $event)"
                  />
                </v-col>
              </v-row>
              <v-data-table
                dense
                :headers="rowHeaders"
                :items="batchStateFor(item.id).rows"
                :loading="batchStateFor(item.id).loading"
                loading-text="Loading rows..."
                :items-per-page="25"
                :footer-props="rowFooterProps"
                class="px-4"
              >
                <template v-slot:item.assignment="{ item: row }">
                  <span v-if="row.assignmentId">#{{ row.assignmentId }}</span>
                  <span v-else class="grey--text">{{ row.sourceAssignmentId || row.sourceVmsAssignmentId || '—' }}</span>
                </template>
                <template v-slot:item.workDate="{ item: row }">
                  <span v-if="row.workDate">{{ row.workDate | formatDate }}</span>
                  <span v-else>{{ row.sourceWorkDate || '—' }}</span>
                </template>
                <template v-slot:item.outcomeName="{ item: row }">
                  <v-chip
                    x-small
                    label
                    :color="outcomeAttrs(row.outcomeId).color"
                    :dark="outcomeAttrs(row.outcomeId).dark"
                  >{{ row.outcomeName }}</v-chip>
                </template>
                <template v-slot:item.failure="{ item: row }">
                  <span v-if="row.failureReasonName" class="red--text">{{ row.failureReasonName }}</span>
                  <div v-if="row.failureDetail" class="caption grey--text">{{ row.failureDetail }}</div>
                </template>
              </v-data-table>
            </td>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import Vue from 'vue'
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { BottomSpacer } from '@/components/buttons'
import api from '@/lib/api'
import { notifyError } from '@/lib/notifications'
import { PATH_IMPORT_BATCHES } from '@/lib/constants/api'
import { importsRoute } from '@/lib/constants/routes'
import { IMPORT_ROW_OUTCOME_OPTIONS, importRunStatus, importRunStatusAttrs, importRowOutcomeAttrs } from '@/lib/constants/imports'

export default {
  components: {
    BottomSpacer
  },
  data () {
    return {
      outcomeOptions: IMPORT_ROW_OUTCOME_OPTIONS,
      expanded: [],
      batchState: {},
      rowFooterProps: { 'items-per-page-options': [25, 50, 100, -1] },
      batchHeaders: [
        { text: 'Batch', value: 'id', sortable: false, width: '8%' },
        { text: 'File', value: 'fileName', sortable: false, width: '18%' },
        { text: 'Status', value: 'statusName', align: 'center', sortable: false, width: '9%' },
        { text: 'Imported / Failed / Skipped / Total', value: 'rows', align: 'center', sortable: false, width: '15%' },
        { text: 'Created', value: 'createdAt', align: 'center', sortable: false, width: '11%' },
        { text: 'Downloads', value: 'downloads', align: 'center', sortable: false, width: '9%' },
        { text: 'Actions', value: 'actions', align: 'center', sortable: false, width: '22%' }
      ],
      rowHeaders: [
        { text: 'Row', value: 'rowNumber', sortable: false, width: '6%' },
        { text: 'Consultant', value: 'sourcePersonName', sortable: false, width: '16%' },
        { text: 'Assignment', value: 'assignment', sortable: false, width: '12%' },
        { text: 'Work Date', value: 'workDate', align: 'center', sortable: false, width: '10%' },
        { text: 'Time Type', value: 'timeTypeName', align: 'center', sortable: false, width: '8%' },
        { text: 'Hours', value: 'hours', align: 'center', sortable: false, width: '7%' },
        { text: 'Previous', value: 'previousHours', align: 'center', sortable: false, width: '7%' },
        { text: 'Outcome', value: 'outcomeName', align: 'center', sortable: false, width: '10%' },
        { text: 'Failure', value: 'failure', sortable: false, width: '24%' }
      ]
    }
  },
  computed: {
    ...mapFields('importRun', ['run', 'batches']),
    // Skipped rows are recorded per Batch (a Custom Import Format deliberately leaves some out), not on
    // the Run, so the Run total is the sum across the Batches this Run owns. Scoped to this Run's own
    // Batches because a Replay or Re-read Run's detail also lists the origin Upload Batch it re-resolved
    // (via ReplayedBatchId) -- counting that here would add a foreign Batch's skips to a total whose
    // Imported/Failed/Total come from this Run alone. Zero for a standard upload or a vendor feed.
    runSkippedRows () {
      if (!this.run) return 0
      return (this.batches || [])
        .filter(batch => batch.vmsIntegrationRunId === this.run.id)
        .reduce((total, batch) => total + (batch.skippedRows || 0), 0)
    },
    breadcrumbs () {
      return [
        { text: 'Imports', to: { name: importsRoute.name } },
        { text: this.run ? `Run #${this.run.id}` : 'Run', disabled: true }
      ]
    }
  },
  watch: {
    batches: {
      immediate: true,
      handler (batches) {
        (batches || []).forEach(batch => {
          if (!this.batchState[batch.id]) {
            Vue.set(this.batchState, batch.id, { outcomeId: null, rows: [], loading: false, loaded: false })
          }
        })
      }
    }
  },
  methods: {
    ...mapActions('importRun', ['getRun', 'getBatchRows', 'clearRun', 'importBatch', 'recheckAssignments', 'rereadFile']),
    statusAttrs (statusId) {
      return importRunStatusAttrs(statusId)
    },
    isValidated (batch) {
      return batch.statusId === importRunStatus.Validated
    },
    isImported (batch) {
      return batch.statusId === importRunStatus.Success
    },
    // Re-read rebuilds the Rows from the stored original, so it is offered only for an upload that
    // still has one and has not yet been imported: the server refuses a Success or Running Batch.
    canReRead (batch) {
      return this.hasOriginal(batch) &&
        batch.statusId !== importRunStatus.Success &&
        batch.statusId !== importRunStatus.Running
    },
    // Each recovery re-resolves or rebuilds the Batch in place and opens its own Run in the list, so
    // the current Run's detail is what shows the Batch's new state; reload it after the action.
    refreshRun () {
      this.expanded = []
      this.batchState = {}
      return this.getRun({ id: parseInt(this.$route.params.id) })
    },
    importBatchAndRefresh (batchId) {
      return this.importBatch({ batchId })
        .then(() => this.refreshRun())
        .catch(() => {})
    },
    recheckAssignmentsAndRefresh (batchId) {
      return this.recheckAssignments({ batchId })
        .then(() => this.refreshRun())
        .catch(() => {})
    },
    rereadAndRefresh (batchId) {
      return this.rereadFile({ batchId })
        .then(() => this.refreshRun())
        .catch(() => {})
    },
    outcomeAttrs (outcomeId) {
      return importRowOutcomeAttrs(outcomeId)
    },
    batchStateFor (batchId) {
      return this.batchState[batchId] || { outcomeId: null, rows: [], loading: false, loaded: false }
    },
    hasOriginal (batch) {
      return !!(batch.uploadObjectKey || batch.uploadedFileName)
    },
    hasPayload (batch) {
      return !!batch.rawPayload
    },
    openDownload (batchId, kind) {
      return api.doGetFile({ path: `${PATH_IMPORT_BATCHES}/${batchId}/${kind}` })
        .catch(error => notifyError(this.$store.dispatch, error.message || 'Unable to download the file.'))
    },
    onBatchExpanded ({ item, value }) {
      if (value && !this.batchStateFor(item.id).loaded) {
        this.loadRows(item.id)
      }
    },
    loadRows (batchId) {
      const state = this.batchState[batchId]
      if (!state) return
      state.loading = true
      return this.getBatchRows({ batchId, outcomeId: state.outcomeId })
        .then(rows => {
          state.rows = rows
          state.loaded = true
        })
        .finally(() => {
          state.loading = false
        })
    },
    changeOutcome (batchId, outcomeId) {
      const state = this.batchState[batchId]
      if (!state) return
      state.outcomeId = outcomeId
      this.loadRows(batchId)
    }
  },
  async created () {
    const id = parseInt(this.$route.params.id)
    await this.getRun({ id })
  },
  beforeDestroy () {
    this.clearRun()
  }
}
</script>
